/**
 * The hub's Bridge tab (docs/ux/bridge.md): the four-step strip, one action
 * for the step the matter is on (record or drop the audio; package; return
 * and ingest the template), the files as retained evidence, and the ingest
 * report. The matter stays at 6; the tab never edits the report.
 *
 * D-0057: the recordings and the returned templates are lists. A failed
 * ingest is retried by returning another file; several files can be dropped
 * and the admin chooses which to read; a file can be removed from the bridge
 * (the object stays as evidence, so the word is Remove, never Delete).
 */

import { statusLabel } from '@beam/core/status';
import { CheckIcon, CircleIcon, MicIcon, SquareIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { say } from '@/app/announcer';
import type { BridgeAudioFile, BridgeFile, BridgeReturnedFile, BridgeView, MatterDetail } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { dateTime, pluralise } from '@/lib/format';
import { Panel } from '../panels';
import { BUSY_WORD, NEVER_RECORDED, REMOVE_BODY, SHOWN_HERE_ONLY, STEPS, durationWord, elapsedWord, failureSentence, formatBytes, outcomeSentence, providerWord, reached, transcriptCountsLine, transcriptOutcome } from './describe';
import { UploadRefused, bridgeBusy, uploadBridgeFile, useBridge, useIngest, usePackage, useRemoveFile, useTranscribe, useTranscript } from './useBridge';

const AUDIO_ACCEPT = { mime: ['audio/webm', 'video/webm', 'audio/mp4', 'audio/x-m4a', 'audio/mpeg', 'audio/wav', 'audio/x-wav', 'audio/ogg'], extensions: '.webm,.m4a,.mp4,.mp3,.wav,.ogg', words: 'webm, m4a, mp3, wav or ogg', max: 200 * 1024 * 1024 };
const BY_EXTENSION: Record<string, string> = { webm: 'audio/webm', m4a: 'audio/mp4', mp4: 'audio/mp4', mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg' };

/** Browsers label files by extension (a .webm as video); the extension decides the audio container we send. */
function audioType(file: File): string | null {
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  return BY_EXTENSION[ext] ?? (AUDIO_ACCEPT.mime.includes(file.type) ? file.type : null);
}
const DOCX_ACCEPT = { mime: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'], extensions: '.docx', words: '.docx', max: 40 * 1024 * 1024 };

const live = (job: { state: string } | null | undefined): boolean => Boolean(job && (job.state === 'queued' || job.state === 'running'));

function Strip({ view }: { view: BridgeView }) {
  return (
    <ol data-slot="bridge-strip" className="flex flex-col gap-2 md:flex-row md:items-start md:gap-0" aria-label="Bridge steps">
      {STEPS.map((step, i) => {
        const done = reached(view.mode, step.mode);
        const current = view.mode === step.mode;
        const at = step.mode === 'audio_in' ? view.audio?.at : step.mode === 'packaged' ? view.package?.at : step.mode === 'returned' ? view.returned?.at : view.ingest?.at;
        return (
          <li key={step.mode} data-step={step.mode} data-reached={done} aria-current={current ? 'step' : undefined} className="flex items-center gap-2 md:flex-1 md:flex-col md:items-start">
            <span className="flex items-center gap-2 md:w-full">
              <span className={`flex size-6 shrink-0 items-center justify-center rounded-full border ${done ? 'border-beam-signature bg-beam-signature text-beam-cream' : 'border-beam-line bg-beam-paper text-beam-text-muted'}`} aria-hidden="true">
                {done ? <CheckIcon className="size-3.5" /> : <CircleIcon className="size-2" />}
              </span>
              {i < STEPS.length - 1 ? <span aria-hidden="true" className={`hidden h-px flex-1 md:block ${reached(view.mode, STEPS[i + 1]!.mode) ? 'bg-beam-signature' : 'bg-beam-line'}`} /> : null}
            </span>
            <span className="flex flex-col leading-tight">
              <span className={`text-sm ${done ? 'font-medium' : 'text-beam-text-muted'}`}>{step.word}</span>
              {at ? <span className="text-xs text-beam-text-muted tabular">{dateTime(at)}</span> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

type RecorderState = 'idle' | 'requesting' | 'recording' | 'uploading' | 'unsupported';

function Recorder({ matterRef, offline, onLanded, onError }: { matterRef: string; offline: boolean; onLanded: () => void; onError: (m: string) => void }) {
  const phone = useIsPhone();
  const supported = typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
  const [state, setState] = useState<RecorderState>(supported ? 'idle' : 'unsupported');
  const [seconds, setSeconds] = useState(0);
  const [percent, setPercent] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => () => { if (timer.current) clearInterval(timer.current); recorder.current?.stream.getTracks().forEach((t) => t.stop()); }, []);

  async function start() {
    setState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (timer.current) clearInterval(timer.current);
        const type = rec.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunks.current, { type });
        setState('uploading');
        try {
          await uploadBridgeFile(matterRef, 'audio', blob, type.split(';')[0]!, setPercent);
          say('Audio uploaded.');
          onLanded();
        } catch (err) {
          onError(err instanceof UploadRefused ? (err.reason ?? 'The upload was refused.') : 'The upload did not complete.');
          setState('idle');
        }
      };
      recorder.current = rec;
      rec.start(1000);
      setSeconds(0);
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      setState('recording');
      say('Recording.');
    } catch {
      onError('The microphone could not be opened; drop a file instead.');
      setState('idle');
    }
  }

  if (state === 'unsupported') return <p className="text-sm text-beam-text-muted">This browser cannot record; drop a file instead.</p>;
  const recording = state === 'recording';
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          data-slot="recorder"
          size="lg"
          variant={recording ? 'destructive' : 'default'}
          className={phone ? 'h-14 w-full' : 'h-11'}
          aria-pressed={recording}
          disabled={offline || state === 'requesting' || state === 'uploading'}
          onClick={() => { if (recording) recorder.current?.stop(); else void start(); }}
        >
          {recording ? <SquareIcon aria-hidden="true" className="size-4" /> : <MicIcon aria-hidden="true" className="size-4" />}
          {state === 'requesting' ? 'Opening the microphone' : state === 'uploading' ? 'Uploading' : recording ? 'Stop and upload' : 'Start recording'}
        </Button>
        <span aria-live="polite" className={`font-heading tabular text-[26px] ${recording ? 'text-beam-error' : 'text-beam-text-muted'}`}>{elapsedWord(seconds)}</span>
      </div>
      {state === 'uploading' ? <Progress value={percent} aria-label="Upload progress" /> : null}
    </div>
  );
}

/** A drop zone that takes several files at once (D-0057); each is uploaded in turn. */
function FileDrop({ label, accept, disabled, onFiles }: { label: string; accept: typeof AUDIO_ACCEPT | typeof DOCX_ACCEPT; disabled: boolean; onFiles: (files: File[]) => void }) {
  const phone = useIsPhone();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const pick = (list: FileList | null | undefined) => { const files = Array.from(list ?? []); if (files.length > 0) onFiles(files); };
  const onDrop = (e: DragEvent) => { e.preventDefault(); setOver(false); if (!disabled) pick(e.dataTransfer.files); };
  return (
    <div
      data-slot="file-drop"
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={`flex flex-col gap-2 rounded-lg border border-dashed p-3 ${over ? 'border-beam-signature bg-beam-cream-deep' : 'border-beam-line-strong'}`}
    >
      <span className="text-sm">{label}</span>
      <span className="text-xs text-beam-text-muted">{accept.words}, up to {Math.round(accept.max / (1024 * 1024))} MB. Several files can be dropped at once.</span>
      <input ref={input} type="file" multiple accept={accept.extensions} className="sr-only" aria-label={label} disabled={disabled} onChange={(e: ChangeEvent<HTMLInputElement>) => { pick(e.target.files); e.target.value = ''; }} />
      <Button type="button" variant="outline" size={phone ? 'lg' : 'sm'} className={phone ? 'h-11 w-full' : 'self-start'} disabled={disabled} onClick={() => input.current?.click()}>Choose a file</Button>
    </div>
  );
}

/** One file on the bridge: what it is, who added it and when, its outcome, Download, any extra action, and Remove where removal is allowed. */
function FileRow({ file, href, phone, outcome, removable, offline, busy, onRemove, extra }: { file: BridgeFile; href: string; phone: boolean; outcome?: string; removable: boolean; offline: boolean; busy: boolean; onRemove: (file: BridgeFile) => void; extra?: ReactNode }) {
  const good = outcome?.startsWith('Read into') || outcome?.startsWith('Transcribed');
  return (
    <li data-slot="bridge-file" data-file-id={file.id} data-kind={file.kind} className="flex flex-col gap-2 py-2 md:flex-row md:items-start md:justify-between md:gap-3">
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="break-words text-sm tabular">{file.name} <span className="text-xs text-beam-text-muted">{formatBytes(file.bytes)}</span></span>
        <span className="text-xs text-beam-text-muted">{file.kind === 'package' ? 'Built' : 'Added'} by {file.by.startsWith('job:') ? 'the package job' : file.by}, {dateTime(file.at)}</span>
        {outcome ? <span data-slot="file-outcome" className={`text-xs ${outcome.startsWith('Failed') ? 'text-beam-error' : good ? 'text-beam-success' : 'text-beam-text-muted'}`}>{outcome}</span> : null}
      </span>
      <span className={`flex shrink-0 gap-2 ${phone ? 'flex-col' : 'items-center'}`}>
        <a href={href} download={file.name} aria-label={`Download ${file.name}`} className={buttonVariants({ variant: 'outline', size: phone ? 'lg' : 'sm' }) + (phone ? ' h-11 w-full' : '')}>Download</a>
        {extra}
        {removable ? <Button type="button" variant="ghost" size={phone ? 'lg' : 'sm'} className={phone ? 'h-11 w-full' : ''} disabled={offline || busy} aria-label={`Remove ${file.name} from the bridge`} onClick={() => onRemove(file)}>Remove</Button> : null}
      </span>
    </li>
  );
}

/**
 * The machine transcript (D-0088), shown here and nowhere else: which
 * recording it is, how long, when and who read it, the counts, the text as
 * shown in paragraphs, and the words as heard behind a fold. The actions live
 * on the recording rows and the Next step; this panel reports. Rendered only
 * once a recording exists, so a doctor who dictates in the workspace never
 * meets an empty card.
 */
function TranscriptPanel({ matterRef, view, phone, atSix }: { matterRef: string; view: BridgeView; phone: boolean; atSix: boolean }) {
  const transcribed = view.audioFiles.filter((f) => f.transcript?.job.state === 'succeeded');
  const active = view.audioFiles.find((f) => live(f.transcript?.job)) ?? null;
  const failed = [...view.audioFiles].reverse().find((f) => f.transcript?.job.state === 'failed') ?? null;
  const [shown, setShown] = useState<string | null>(null);
  // The reader's explicit choice holds while that recording is still transcribed; otherwise the newest transcript is shown.
  const current = transcribed.find((f) => f.id === shown) ?? transcribed.at(-1) ?? null;
  const transcript = useTranscript(matterRef, current?.id ?? null);
  // The Next step already announces the running job through its own status line; the panel repeats the words, not the announcement.
  const running = active ? <p className="text-sm">Transcribing {active.name}. {BUSY_WORD[active.transcript!.job.state]}</p> : null;

  let body: ReactNode;
  if (!current) {
    // Off the doctor's status the rows offer no Transcribe, so the sentence must not point at one.
    const fromTheRow = atSix ? ' Transcribe from the recording’s row.' : '';
    body = (
      <div className="flex flex-col gap-3">
        {running}
        {failed && !active ? (
          <Alert role="alert" variant="destructive" data-slot="transcript-failed">
            <AlertDescription>{failed.name} could not be transcribed: {failureSentence(failed.transcript!.code ?? undefined)}{atSix ? ' Try again from the recording’s row.' : ''}</AlertDescription>
          </Alert>
        ) : null}
        {!active && !failed ? <p className="text-sm text-beam-text-muted">{atSix ? `Not transcribed yet.${fromTheRow}` : 'Not transcribed. The report has left the doctor.'}</p> : null}
      </div>
    );
  } else {
    const data = transcript.data ?? null;
    const head = current.transcript!;
    body = (
      <div className="flex flex-col gap-3">
        {running}
        {transcribed.length > 1 ? (
          <RadioGroup value={current.id} onValueChange={setShown} aria-label="Which transcript to show" className="gap-1">
            {transcribed.map((f) => (
              <label key={f.id} className={`flex items-center gap-3 text-sm ${phone ? 'min-h-11' : 'min-h-8'}`}>
                <RadioGroupItem value={f.id} aria-label={f.name} />
                <span className="min-w-0 break-words">{f.name} <span className="text-xs text-beam-text-muted">{f.transcript?.at ? dateTime(f.transcript.at) : ''}</span></span>
              </label>
            ))}
          </RadioGroup>
        ) : null}
        {/* The head already knows the recording, its length, when and who read it: those lines never wait on the text. */}
        <p className="text-sm">{current.name}, {durationWord(head.counts.durationSeconds)}. {providerWord(head.mode)}, {dateTime(head.at ?? view.updatedAt)}.</p>
        <p className="text-xs text-beam-text-muted">{transcriptCountsLine(head.counts)}</p>
        {transcript.isPending ? (
          <div className="flex max-w-[72ch] flex-col gap-3" aria-hidden="true"><Skeleton className="h-4 rounded" /><Skeleton className="h-4 w-5/6 rounded" /><Skeleton className="h-4 w-2/3 rounded" /></div>
        ) : transcript.isError ? (
          <Alert role="alert" variant="destructive">
            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
              <span>The transcript could not be loaded.</span>
              <Button type="button" variant="outline" size="sm" onClick={() => void transcript.refetch()}>Try again</Button>
            </AlertDescription>
          </Alert>
        ) : !data ? (
          <p className="text-sm text-beam-text-muted">The transcript could not be loaded.</p>
        ) : (
          <>
            {/* The one thing on the tab that is read rather than scanned: body size, the body line height, a reading measure (docs/ux/bridge.md, off-system 72ch flagged). */}
            <div data-slot="bridge-transcript-text" className="flex max-w-[72ch] flex-col gap-4 text-base leading-[1.45]">
              {data.text.split(/\n[ \t]*\n/).map((paragraph, i) => <p key={i} className="whitespace-pre-wrap break-words">{paragraph}</p>)}
            </div>
            <Collapsible>
              <CollapsibleTrigger className={`text-sm text-beam-signature underline-offset-2 hover:underline ${phone ? 'min-h-11' : 'min-h-8'}`}>As heard</CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                <p data-slot="bridge-transcript-raw" className="max-w-[72ch] whitespace-pre-wrap break-words text-sm text-beam-text-muted">{data.raw}</p>
              </CollapsibleContent>
            </Collapsible>
            <p className="text-xs text-beam-text-muted">{SHOWN_HERE_ONLY}</p>
          </>
        )}
      </div>
    );
  }
  // "Machine transcript", because on a Quality Scribe tab the plain word names the scribe's product.
  return <Panel title="Machine transcript" className="mt-6"><div data-slot="bridge-transcript">{body}</div></Panel>;
}

/** Which returned file to read (D-0057): the files not yet read, newest first and chosen by default. */
function IngestChooser({ files, offline, busy, onIngest, phone }: { files: BridgeReturnedFile[]; offline: boolean; busy: boolean; onIngest: (fileId: string) => void; phone: boolean }) {
  const candidates = [...files].reverse().filter((f) => f.ingest?.job.state !== 'succeeded' && !live(f.ingest?.job));
  const [chosen, setChosen] = useState<string | null>(null);
  const selected = candidates.some((c) => c.id === chosen) ? chosen! : candidates[0]?.id ?? null;
  if (candidates.length === 0) return null;
  return (
    <div className="flex flex-col gap-2" data-slot="ingest-chooser">
      {candidates.length > 1 ? (
        <RadioGroup value={selected ?? undefined} onValueChange={setChosen} aria-label="Which returned file to read">
          {candidates.map((f) => (
            <label key={f.id} className={`flex items-center gap-3 text-sm ${phone ? 'min-h-11' : 'min-h-8'}`}>
              <RadioGroupItem value={f.id} aria-label={f.name} />
              <span className="min-w-0 break-words">{f.name} <span className="text-xs text-beam-text-muted">{dateTime(f.at)}, {outcomeSentence(f).charAt(0).toLowerCase() + outcomeSentence(f).slice(1)}</span></span>
            </label>
          ))}
        </RadioGroup>
      ) : <p className="text-sm">{candidates[0]!.name} is ready to read.</p>}
      <Button data-slot="ingest" size={phone ? 'lg' : 'default'} className={phone ? 'h-11 w-full' : 'self-start'} disabled={offline || busy || !selected} aria-busy={busy} onClick={() => { if (selected) onIngest(selected); }}>
        {busy ? 'Reading' : candidates.length > 1 ? 'Ingest the chosen file' : 'Ingest'}
      </Button>
    </div>
  );
}

export function BridgeTab({ matterRef, detail, offline }: { matterRef: string; detail: MatterDetail | undefined; offline: boolean }) {
  const bridge = useBridge(matterRef);
  const phone = useIsPhone();
  const [message, setMessage] = useState<string | null>(null);
  const [uploading, setUploading] = useState<{ which: 'audio' | 'return'; percent: number; index: number; total: number } | null>(null);
  const pack = usePackage(matterRef, setMessage);
  const ingest = useIngest(matterRef, setMessage);
  const remove = useRemoveFile(matterRef, setMessage);
  const transcribe = useTranscribe(matterRef, setMessage);
  const [removing, setRemoving] = useState<BridgeFile | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const view = bridge.data ?? null;
  // The package job's code is on the job record; a returned file's outcome is on its row.
  const failedJob = view?.packageJob?.state === 'failed' ? view.packageJob.jobId : null;
  useEffect(() => {
    if (!failedJob) { setFailure(null); return; }
    let cancelled = false;
    void fetch(`/api/jobs/${encodeURIComponent(failedJob)}`, { credentials: 'same-origin', headers: { accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((job: { error?: { code?: string } } | null) => { if (!cancelled) setFailure(failureSentence(job?.error?.code)); })
      .catch(() => { if (!cancelled) setFailure(failureSentence(undefined)); });
    return () => { cancelled = true; };
  }, [failedJob]);

  // The ingest's completion is announced once per outcome (docs/ux/bridge.md section 9).
  const lastOutcome = useRef<string | null>(null);
  useEffect(() => {
    const latest = view?.returnedFiles.filter((f) => f.ingest && !live(f.ingest.job)).at(-1);
    const key = latest?.ingest ? `${latest.id}:${latest.ingest.job.state}` : null;
    if (key && lastOutcome.current !== null && key !== lastOutcome.current) say(latest!.ingest!.job.state === 'succeeded' ? `Ingested: ${outcomeSentence(latest!)}.` : `The returned file could not be read. ${failureSentence(latest!.ingest!.code ?? undefined)}`);
    lastOutcome.current = key;
  }, [view?.returnedFiles]);

  // A transcript landing or failing is announced once, by name and count; the words themselves are never spoken (D-0088).
  const lastTranscriptStates = useRef<Map<string, string> | null>(null);
  useEffect(() => {
    const files = view?.audioFiles ?? [];
    if (lastTranscriptStates.current) {
      for (const f of files) {
        const before = lastTranscriptStates.current.get(f.id);
        const now = f.transcript?.job.state;
        if ((before === 'queued' || before === 'running') && now !== before) {
          if (now === 'succeeded') say(`Transcribed ${f.name}: ${pluralise(f.transcript!.counts.words, 'word')}.`);
          else if (now === 'failed') say(`${f.name} could not be transcribed. ${failureSentence(f.transcript!.code ?? undefined)}`);
        }
      }
    }
    lastTranscriptStates.current = new Map(files.map((f) => [f.id, f.transcript?.job.state ?? '-']));
  }, [view?.audioFiles]);

  async function upload(which: 'audio' | 'return', files: File[]) {
    const accept = which === 'audio' ? AUDIO_ACCEPT : DOCX_ACCEPT;
    for (const file of files) {
      if (file.size > accept.max) { setMessage(`${file.name} is larger than ${Math.round(accept.max / (1024 * 1024))} MB.`); return; }
      if (which === 'audio' && !audioType(file)) { setMessage(`Send the audio as ${accept.words}.`); return; }
    }
    setMessage(null);
    let landed = 0;
    try {
      for (const [index, file] of files.entries()) {
        const type = which === 'audio' ? audioType(file)! : (file.type || DOCX_ACCEPT.mime[0]!);
        setUploading({ which, percent: 0, index: index + 1, total: files.length });
        await uploadBridgeFile(matterRef, which, file, type || 'application/octet-stream', (p) => setUploading({ which, percent: p, index: index + 1, total: files.length }), undefined, file.name);
        landed += 1;
      }
      say(which === 'audio' ? (landed === 1 ? 'Audio uploaded.' : `${landed} recordings uploaded.`) : landed === 1 ? 'Returned template uploaded.' : `${landed} returned files uploaded.`);
      await bridge.refetch();
    } catch (err) {
      setMessage(err instanceof UploadRefused ? (err.reason ?? 'The upload was refused.') : `The upload did not complete${landed > 0 ? ` after ${landed} of ${files.length}` : ''}. Nothing else changed.`);
      await bridge.refetch();
    } finally {
      setUploading(null);
    }
  }

  if (!detail || bridge.isPending) {
    return <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]"><Skeleton className="h-64 rounded-xl" /><Skeleton className="h-40 rounded-xl" /></div>;
  }
  if (bridge.isError) return <ErrorPanel message="The bridge could not be loaded." onRetry={() => void bridge.refetch()} offline={offline} />;
  if (!view) return <EmptyState title="This case has no bridge." />;

  const status = detail.matter.status;
  const busy = bridgeBusy(view);
  const atSix = status === 6;
  const files = (which: 'audio' | 'package' | 'returned') => `/api/matters/${encodeURIComponent(matterRef)}/bridge/files/${which}`;
  const fileHref = (id: string) => `/api/matters/${encodeURIComponent(matterRef)}/bridge/file/${encodeURIComponent(id)}`;
  const reading = view.returnedFiles.find((f) => live(f.ingest?.job)) ?? null;
  // The machine transcript (D-0088): only when the environment switched it on, only at 6, one recording at a time, never twice.
  const transcribeOn = view.transcription.mode !== 'off';
  const transcribing = view.audioFiles.find((f) => live(f.transcript?.job)) ?? null;
  const newestRecording: BridgeAudioFile | null = view.audioFiles.at(-1) ?? null;
  const canTranscribe = (f: BridgeAudioFile): boolean => transcribeOn && atSix && !transcribing && f.transcript?.job.state !== 'succeeded';
  const startTranscribe = (f: BridgeAudioFile) => { setMessage(null); say(`Transcribing ${f.name}.`); transcribe.mutate(f.id); };
  // Several rows carry the same word, so the label names the recording for a reader who cannot see the row. Beside
  // Package the row's red line is a column away, so the visible word says what is tried again and, with several
  // recordings, which one.
  const transcribeButton = (f: BridgeAudioFile, slot: 'transcribe' | 'transcribe-next', size: 'sm' | 'default') => {
    const failedBefore = f.transcript?.job.state === 'failed';
    const label = slot === 'transcribe' ? (failedBefore ? 'Try again' : 'Transcribe') : failedBefore ? 'Transcribe again' : view.audioFiles.length > 1 ? `Transcribe ${f.name}` : 'Transcribe';
    return (
      <Button type="button" data-slot={slot} data-file-id={f.id} variant="outline" size={phone ? 'lg' : size} className={phone ? 'h-11 w-full' : ''} disabled={offline || transcribe.isPending} aria-busy={transcribe.isPending} aria-label={failedBefore ? `Try again: ${f.name}` : `Transcribe ${f.name}`} onClick={() => startTranscribe(f)}>
        {label}
      </Button>
    );
  };
  const newestReturned = view.returnedFiles.at(-1) ?? null;
  const newestFailed = newestReturned?.ingest?.job.state === 'failed' ? newestReturned : null;
  const returnLabel = view.returnedFiles.length > 0 ? 'Return another file' : 'Ingest completed template: drop the returned .docx here';

  const returnSide = atSix ? (
    <div className="flex flex-col gap-3" data-slot="return-side">
      {reading ? <p role="status" className="text-sm">Reading {reading.name}. {BUSY_WORD[reading.ingest!.job.state]}</p> : null}
      {newestFailed && !reading ? (
        <Alert role="alert" variant="destructive" data-slot="ingest-failed">
          <AlertDescription>{newestFailed.name} could not be read into the workspace: {failureSentence(newestFailed.ingest!.code ?? undefined)} The file stays listed as evidence; return another file, or remove it.</AlertDescription>
        </Alert>
      ) : null}
      {!reading ? <IngestChooser files={view.returnedFiles} offline={offline} busy={busy || ingest.isPending} onIngest={(id) => { setMessage(null); say('Reading the returned file.'); ingest.mutate(id); }} phone={phone} /> : null}
      <FileDrop label={returnLabel} accept={DOCX_ACCEPT} disabled={offline || uploading !== null || Boolean(reading)} onFiles={(f) => void upload('return', f)} />
    </div>
  ) : null;

  let next: React.ReactNode;
  if (!view.doctor.onBridge && view.mode === 'none') {
    next = (
      <p className="text-sm text-beam-text-muted">
        {view.doctor.name} dictates in the workspace; the bridge is for doctors on Quality Scribe. <Link to={`/registry/doctors/${encodeURIComponent(view.doctor.slug)}`} className="text-beam-signature underline-offset-2 hover:underline">Open the registry record</Link> to change the dictation mode.
      </p>
    );
  } else if (!atSix && view.mode === 'none') {
    next = <p className="text-sm text-beam-text-muted">{status < 6 ? `The bridge opens when the case reaches 6 ${statusLabel(6)}; it is at ${status} ${statusLabel(status)}.` : 'The report has left the doctor; nothing crossed the bridge on this cycle.'}</p>;
  } else if (!atSix) {
    next = <p className="text-sm text-beam-text-muted">The report has left the doctor; the files below stay with the matter as evidence and nothing more crosses the bridge on this cycle.</p>;
  } else if (view.mode === 'none') {
    next = (
      <div className="flex flex-col gap-4">
        <Recorder matterRef={matterRef} offline={offline} onLanded={() => void bridge.refetch()} onError={setMessage} />
        <FileDrop label="Or drop a dictaphone file" accept={AUDIO_ACCEPT} disabled={offline || uploading !== null} onFiles={(f) => void upload('audio', f)} />
      </div>
    );
  } else if (view.mode === 'audio_in') {
    next = (
      <div className="flex flex-col gap-3">
        <p className="text-sm">Audio in at {dateTime(view.audio!.at)}. Admin packages it for Quality Scribe{view.audioFiles.length > 1 ? `; the package carries the newest recording, ${view.audio!.name}` : ''}.</p>
        <div className={`flex gap-2 ${phone ? 'flex-col' : 'flex-wrap items-center'}`}>
          <Button data-slot="package" size={phone ? 'lg' : 'default'} className={phone ? 'h-11 w-full' : ''} disabled={offline || busy || pack.isPending} aria-busy={busy || pack.isPending} onClick={() => { setMessage(null); say('Building the package.'); pack.mutate(); }}>
            {live(view.packageJob) || pack.isPending ? 'Building the package' : 'Package for Quality Scribe'}
          </Button>
          {newestRecording && canTranscribe(newestRecording) ? transcribeButton(newestRecording, 'transcribe-next', 'default') : null}
        </div>
        {live(view.packageJob) ? <p role="status" className="text-xs text-beam-text-muted">{BUSY_WORD[view.packageJob!.state]}</p> : null}
        {transcribing ? <p role="status" className="text-xs text-beam-text-muted">Transcribing {transcribing.name}. {BUSY_WORD[transcribing.transcript!.job.state]}</p> : null}
        <Collapsible>
          <CollapsibleTrigger className={`text-sm text-beam-signature underline-offset-2 hover:underline ${phone ? 'min-h-11' : 'min-h-8'}`}>Wrong recording? Add another</CollapsibleTrigger>
          <CollapsibleContent className="flex flex-col gap-4 pt-3">
            <p className="text-xs text-beam-text-muted">Record or drop another, then remove the wrong one from the list. The recordings are fixed once the package is built.</p>
            <Recorder matterRef={matterRef} offline={offline || busy} onLanded={() => void bridge.refetch()} onError={setMessage} />
            <FileDrop label="Or drop another dictaphone file" accept={AUDIO_ACCEPT} disabled={offline || busy || uploading !== null} onFiles={(f) => void upload('audio', f)} />
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  } else if (view.mode === 'packaged') {
    next = (
      <div className="flex flex-col gap-3">
        <p className="text-sm">The package is ready for Quality Scribe.</p>
        <div className="flex flex-wrap gap-2">
          <a href={files('package')} download="package.zip" className={buttonVariants({ size: phone ? 'lg' : 'default' }) + (phone ? ' h-11 w-full' : '')}>Download package</a>
        </div>
        {returnSide}
      </div>
    );
  } else if (view.mode === 'returned') {
    next = returnSide;
  } else {
    next = (
      <div className="flex flex-col gap-3">
        <p className="text-sm">Ingested at {dateTime(view.ingest!.at ?? view.updatedAt)}: {view.ingest!.filled} {view.ingest!.filled === 1 ? 'box' : 'boxes'} filled, {view.ingest!.unmapped} unmapped {view.ingest!.unmapped === 1 ? 'paragraph' : 'paragraphs'}.</p>
        <Link to={`/matters/${encodeURIComponent(matterRef)}/workspace`} className={buttonVariants({ size: phone ? 'lg' : 'default' }) + (phone ? ' h-11 w-full' : ' self-start')}>Open the workspace</Link>
        <Collapsible>
          <CollapsibleTrigger className={`text-sm text-beam-signature underline-offset-2 hover:underline ${phone ? 'min-h-11' : 'min-h-8'}`}>Return another file</CollapsibleTrigger>
          <CollapsibleContent className="pt-3">
            <p className="mb-3 text-xs text-beam-text-muted">A further file is read the same way; text the doctor has typed is kept.</p>
            {returnSide}
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  }

  const removable = (file: BridgeFile): boolean => {
    if (!atSix) return false;
    if (file.kind === 'package') return false;
    // A recording being transcribed is refused by the server (409); the row hides Remove for those seconds, as a returned file being read does.
    if (file.kind === 'audio') return !view.package && !live(view.audioFiles.find((f) => f.id === file.id)?.transcript?.job);
    const returned = view.returnedFiles.find((f) => f.id === file.id);
    return !live(returned?.ingest?.job);
  };
  const anyFile = view.audioFiles.length > 0 || view.package || view.returnedFiles.length > 0;
  const removingIngested = removing ? view.returnedFiles.find((f) => f.id === removing.id)?.ingest?.job.state === 'succeeded' : false;
  const removingTranscribed = removing ? view.audioFiles.find((f) => f.id === removing.id)?.transcript?.job.state === 'succeeded' : false;

  return (
    <div className="flex flex-col gap-6">
      <section aria-label="Quality Scribe bridge" className="rounded-xl border border-beam-line bg-beam-paper p-4">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="eyebrow text-beam-text-muted">Quality Scribe bridge</h2>
          <span className="text-xs text-beam-text-muted">{view.doctor.onBridge ? `${view.doctor.name} is on the bridge` : `${view.doctor.name} dictates in the workspace`}</span>
        </div>
        <Strip view={view} />
      </section>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className={phone ? 'order-1' : ''}>
          <Panel title="Next step">
            <div className="flex flex-col gap-3">
              {next}
              {uploading ? <Progress value={uploading.percent} aria-label={uploading.total > 1 ? `Upload progress, file ${uploading.index} of ${uploading.total}` : 'Upload progress'} /> : null}
              {message ? <Alert role="alert" variant="destructive" data-slot="bridge-message"><AlertDescription>{message}</AlertDescription></Alert> : null}
              {failure && !busy ? (
                <Alert role="alert" variant="destructive" data-slot="bridge-failed">
                  <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                    <span>The last step failed: {failure}</span>
                    {view.mode === 'audio_in' ? <Button variant="outline" size="sm" disabled={offline} onClick={() => { setMessage(null); pack.mutate(); }}>Try again</Button> : null}
                  </AlertDescription>
                </Alert>
              ) : null}
              <p className="text-xs text-beam-text-muted">{NEVER_RECORDED}</p>
            </div>
          </Panel>
          {transcribeOn && view.audioFiles.length > 0 ? <TranscriptPanel matterRef={matterRef} view={view} phone={phone} atSix={atSix} /> : null}
        </div>
        <div className={phone ? 'order-2' : ''}>
          <div className="flex flex-col gap-6">
            <Panel title="Files (retained as evidence)">
              {anyFile ? (
                <ul className="flex flex-col divide-y divide-beam-line" data-slot="bridge-pair">
                  {view.audioFiles.map((f) => <FileRow key={f.id} file={f} href={fileHref(f.id)} phone={phone} outcome={transcribeOn ? transcriptOutcome(f) : undefined} removable={removable(f)} offline={offline} busy={remove.isPending} onRemove={setRemoving} extra={canTranscribe(f) ? transcribeButton(f, 'transcribe', 'sm') : null} />)}
                  {view.package ? <FileRow file={view.package} href={files('package')} phone={phone} removable={false} offline={offline} busy={remove.isPending} onRemove={setRemoving} /> : null}
                  {view.returnedFiles.map((f) => <FileRow key={f.id} file={f} href={fileHref(f.id)} phone={phone} outcome={outcomeSentence(f)} removable={removable(f)} offline={offline} busy={remove.isPending} onRemove={setRemoving} />)}
                </ul>
              ) : <p className="text-sm text-beam-text-muted">Nothing is stored yet.</p>}
              {view.audioFiles.length > 0 && view.package ? <p className="mt-2 text-xs text-beam-text-muted">The recordings are fixed: the package the scribe holds carries {view.audio!.name}.</p> : null}
            </Panel>
            <Panel title="Ingest report">
              {view.ingest && view.ingest.job.state === 'succeeded' ? (
                <p data-slot="ingest-report" className="text-sm">{view.ingest.filled} {view.ingest.filled === 1 ? 'box' : 'boxes'} filled from {view.ingest.paragraphs} paragraphs; {view.ingest.unmapped} unmapped {view.ingest.unmapped === 1 ? 'paragraph' : 'paragraphs'}{view.ingest.workspaceVersion ? `; report version ${view.ingest.workspaceVersion}` : ''}{view.returnedFiles.length > 1 ? `; read from ${view.returned?.name ?? 'the returned file'}` : ''}.</p>
              ) : <p className="text-sm text-beam-text-muted">Not yet ingested.</p>}
            </Panel>
          </div>
        </div>
      </div>
      <AlertDialog open={removing !== null} onOpenChange={(o) => { if (!o) setRemoving(null); }}>
        <AlertDialogContent className="sm:max-w-[560px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing?.name} from the bridge?</AlertDialogTitle>
            <AlertDialogDescription>{REMOVE_BODY}{removingIngested ? ' The text already read into the workspace stays there.' : ''}{removingTranscribed ? ' Its machine transcript leaves this tab with it; the text is kept.' : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus className={phone ? 'h-11' : ''}>Keep it</AlertDialogCancel>
            <AlertDialogAction className={phone ? 'h-11' : ''} onClick={() => { if (removing) { setMessage(null); remove.mutate(removing.id, { onSuccess: () => say(`${removing.name} removed from the bridge; the file is kept as evidence.`) }); } setRemoving(null); }}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
